import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useBusinessStore } from '../store/useBusinessStore';
import { calculateSaldoPendiente } from '../utils/depositBalance';
import { 
  Calendar, Clock, User, Phone, CheckCircle2, XCircle, AlertCircle,
  RefreshCw, Search, CalendarDays, Filter, Plus, UserCircle2, 
  Sparkles, MessageSquare, Trash2, Edit3, X, Check, Send,
  Layers, ExternalLink, ChevronDown, ListFilter, CalendarRange
} from 'lucide-react';
import toast from 'react-hot-toast';

export function AppointmentsView({ negocioId }) {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  
  // Buscar negocio activo
  const activeBusiness = useMemo(() => {
    const targetId = negocioId || activeBusinessId;
    return masterDb.find(b => String(b.id) === String(targetId)) || masterDb[0] || null;
  }, [masterDb, negocioId, activeBusinessId]);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all'); // 'all' | 'today' | 'tomorrow' | 'this_week' | 'future' | 'past'
  const [filterStaff, setFilterStaff] = useState('all');

  // Modales
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Formulario Nueva Cita
  const initialFormState = {
    client: '',
    phone: '',
    service: '',
    customService: '',
    staffassigned: '',
    date: new Date().toISOString().split('T')[0],
    time: '10:00 AM',
    status: 'Confirmada',
    source: 'panel_manual',
    notes: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // Horarios sugeridos para selección rápida
  const quickTimeSlots = [
    '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM', '07:00 PM'
  ];

  // Generar citas demo contextuales si la base de datos está vacía
  const getInitialDemoAppointments = (biz) => {
    const today = new Date();
    const formatDateStr = (offsetDays) => {
      const d = new Date(today);
      d.setDate(d.getDate() + offsetDays);
      return d.toISOString().split('T')[0];
    };

    const srv1 = biz?.services?.[0]?.nombre || 'Corte Degradado + Barba';
    const srv2 = biz?.services?.[1]?.nombre || 'Perfilado de Barba';
    const srv3 = biz?.services?.[2]?.nombre || 'Asesoría y Tratamiento';
    const staff1 = biz?.staff?.[0]?.name || 'Carlos Barbero';
    const staff2 = biz?.staff?.[1]?.name || 'Juan Especialista';

    return [
      {
        id: 'demo-app-1',
        business_id: String(biz?.id || 'demo'),
        client: 'Andrés Felipe Morales',
        phone: '573104558899',
        service: srv1,
        date: formatDateStr(0),
        time: '10:30 AM',
        staffassigned: staff1,
        status: 'Confirmada',
        source: 'whatsapp_ia',
        created_at: new Date().toISOString()
      },
      {
        id: 'demo-app-2',
        business_id: String(biz?.id || 'demo'),
        client: 'Mateo Rodríguez',
        phone: '573158876655',
        service: srv2,
        date: formatDateStr(0),
        time: '03:00 PM',
        staffassigned: staff2,
        status: 'Pendiente',
        source: 'whatsapp_ia',
        created_at: new Date().toISOString()
      },
      {
        id: 'demo-app-3',
        business_id: String(biz?.id || 'demo'),
        client: 'Valentina Gómez',
        phone: '573209988776',
        service: srv3,
        date: formatDateStr(1),
        time: '11:00 AM',
        staffassigned: staff1,
        status: 'Confirmada',
        source: 'minisitio_web',
        created_at: new Date().toISOString()
      },
      {
        id: 'demo-app-4',
        business_id: String(biz?.id || 'demo'),
        client: 'David Restrepo',
        phone: '573001239988',
        service: srv1,
        date: formatDateStr(-1),
        time: '04:30 PM',
        staffassigned: staff2,
        status: 'Completada',
        source: 'panel_manual',
        created_at: new Date(Date.now() - 86400000).toISOString()
      }
    ];
  };

  // Cargar Citas (Supabase + Fallback LocalStorage)
  const fetchAppointments = async () => {
    if (!activeBusiness) return;
    setLoading(true);
    const storageKey = `seryiflow_appointments_${activeBusiness.id}`;
    const cachedLocal = localStorage.getItem(storageKey);

    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('business_id', String(activeBusiness.id))
        .order('date', { ascending: true });

      if (!error && data && data.length > 0) {
        // Normalizar nombres de columnas por si vienen con schema antiguo
        const normalized = data.map(item => ({
          ...item,
          client: item.client || item.client_name || 'Cliente',
          phone: item.phone || item.client_phone || '',
          service: item.service || 'Servicio General',
          date: item.date || (item.start_time ? item.start_time.split('T')[0] : new Date().toISOString().split('T')[0]),
          time: item.time || (item.start_time ? new Date(item.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '10:00 AM'),
          staffassigned: item.staffassigned || 'Cualquiera',
          status: item.status || 'Confirmada',
          source: item.source || 'whatsapp_ia',
          google_event_id: item.google_event_id || null
        }));
        setAppointments(normalized);
        saveToLocal(normalized);
      } else {
        // Si no hay datos en Supabase, cargar desde cache local o generar demos
        if (cachedLocal) {
          try {
            setAppointments(JSON.parse(cachedLocal));
          } catch (e) {
            const demos = getInitialDemoAppointments(activeBusiness);
            setAppointments(demos);
            localStorage.setItem(storageKey, JSON.stringify(demos));
          }
        } else {
          const demos = getInitialDemoAppointments(activeBusiness);
          setAppointments(demos);
          localStorage.setItem(storageKey, JSON.stringify(demos));
        }
      }
    } catch (err) {
      console.warn("Supabase fetch appointments fallback to local cache:", err);
      if (cachedLocal) {
        setAppointments(JSON.parse(cachedLocal));
      } else {
        setAppointments(getInitialDemoAppointments(activeBusiness));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchAppointments();
    }
  }, [activeBusiness?.id]);

  // Guardar en LocalStorage helper y sincronizar con Master DB (para Kanban)
  const saveToLocal = (newList) => {
    if (!activeBusiness) return;
    localStorage.setItem(`seryiflow_appointments_${activeBusiness.id}`, JSON.stringify(newList));
    setMasterDb(prev => {
      if (!Array.isArray(prev)) return prev;
      return prev.map(b => String(b.id) === String(activeBusiness.id) ? { ...b, appointments: newList } : b);
    });
  };

  // Sincronizar Citas con Google Calendar
  const syncAppointmentsToGoogle = async () => {
    if (!activeBusiness?.id) return;
    setLoading(true);
    let errorCount = 0;
    
    try {
      // 1. Consultar a Supabase las citas pendientes
      const { data: pendingAppointments, error: fetchError } = await supabase
        .from('appointments')
        .select('*')
        .is('google_event_id', null)
        .eq('business_id', activeBusiness.id)
        .in('status', ['scheduled', 'Confirmada']);

      if (fetchError) throw fetchError;
      
      const toSync = pendingAppointments || [];

      if (toSync.length > 0) {
        toast.loading(`Sincronizando ${toSync.length} citas vía n8n...`, { id: 'sync-google' });
        const targetWebhook = activeBusiness?.webhook_n8n || "https://flow.seryiads.com/webhook/keira-router-central";

        for (const app of toSync) {
          try {
            // Parsear FECHA_Y_HORA_ISO 
            let startIso = app.start_time || new Date().toISOString();
            let endIso = app.end_time || new Date(new Date(startIso).getTime() + 60*60*1000).toISOString();
            
            try {
               if(app.date && app.time && !app.start_time) {
                 const dateTimeStr = `${app.date} ${app.time}`;
                 startIso = new Date(dateTimeStr).toISOString();
                 endIso = new Date(new Date(startIso).getTime() + 60*60*1000).toISOString();
               }
            } catch(e) {}

            // 2. Armar el payload solicitado
            const webhookPayload = {
              calendar_id: activeBusiness?.google_calendar_id || activeBusiness?.id,
              client_phone: app.phone || app.client_phone || "000000000",
              client_name: app.client || app.client_name || "Cliente Desconocido",
              service: app.service || "Cita General",
              start_time: startIso,
              end_time: endIso,
              staff_name: app.staff_id || app.staffassigned || "Cualquiera",
              origen: "crm"
            };

            const res = await fetch(targetWebhook, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(webhookPayload)
            });
            
            try {
              if (res.ok) {
                const resData = await res.json();
                if (resData && (resData.google_event_id || resData.id)) {
                  await supabase
                    .from('appointments')
                    .update({ google_event_id: resData.google_event_id || resData.id })
                    .eq('id', app.id);
                }
              }
            } catch (err) {
              console.warn("No se pudo leer la respuesta JSON de n8n o no devolvió un ID.");
            }
              
          } catch (err) {
            errorCount++;
            console.error(`Fallo sincronizando a ${app.client}:`, err);
          }
        }
        
        if (errorCount === 0) {
          toast.success(`✅ ${toSync.length} citas enviadas a Google Calendar.`, { id: 'sync-google' });
        } else {
          toast.error(`⚠️ Se sincronizaron citas pero hubo ${errorCount} errores.`, { id: 'sync-google' });
        }
      } else {
        toast.success("Agenda actualizada. No hay citas pendientes.", { id: 'sync-google' });
      }
    } catch (globalErr) {
      toast.error(`Error consultando citas pendientes: ${globalErr.message}`, { id: 'sync-google' });
    } finally {
      // Refrescar finalmente SIEMPRE
      await fetchAppointments();
      setLoading(false);
    }
  };

  // Crear Cita Manual
  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!formData.client.trim()) {
      toast.error("Por favor ingresa el nombre del cliente.");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Por favor ingresa el WhatsApp / Teléfono del cliente.");
      return;
    }

    // Validar que la fecha y hora no sean en el pasado
    try {
      const dateStr = formData.date || new Date().toISOString().split('T')[0];
      const timeStr = formData.time || '10:00 AM';
      
      const [timeMatch, modifier] = timeStr.split(' ');
      if (timeMatch && modifier) {
        let [hours, minutes] = timeMatch.split(':');
        if (hours === '12') hours = '00';
        if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
        
        const appointmentDate = new Date(`${dateStr}T${hours.toString().padStart(2, '0')}:${minutes}:00`);
        if (appointmentDate < new Date()) {
          toast.error("No puedes agendar una cita en el pasado (fecha u hora ya superada).");
          return;
        }
      }
    } catch (e) {
      console.warn("Error validando fecha pasada", e);
    }

    const serviceFinal = formData.service === 'OTRO' 
      ? (formData.customService.trim() || 'Servicio Personalizado')
      : (formData.service || activeBusiness?.services?.[0]?.nombre || 'Servicio General');

    const newAppointment = {
      id: 'app-' + Date.now(),
      business_id: String(activeBusiness.id),
      client: formData.client.trim(),
      phone: formData.phone.trim().replace(/\D/g, ''),
      service: serviceFinal,
      staffassigned: formData.staffassigned || (activeBusiness?.staff?.[0]?.name || 'Cualquiera'),
      date: formData.date || new Date().toISOString().split('T')[0],
      time: formData.time || '10:00 AM',
      status: formData.status || 'Confirmada',
      source: 'panel_manual',
      created_at: new Date().toISOString()
    };

    setIsSubmitting(true);

    // Optimistic Update
    const updatedList = [newAppointment, ...appointments];
    setAppointments(updatedList);
    saveToLocal(updatedList);

    // Intentar sincronizar con Supabase
    try {
      const { error } = await supabase.from('appointments').insert([newAppointment]);
      if (error) {
        console.warn("[Supabase App Insert Warning]:", error.message);
        toast.success("✅ Cita agendada en tu panel");
      } else {
        toast.success("✨ ¡Cita agendada y sincronizada en la nube!");
        
        // 3. Notificar a n8n para crearla en Google Calendar
        const createWebhookUrl = import.meta.env.VITE_N8N_CREATE_WEBHOOK_URL;
        if (createWebhookUrl) {
          fetch(createWebhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newAppointment)
          }).then(() => {
            console.log("Evento enviado a crear en Google Calendar via n8n");
          }).catch(webhookErr => {
            console.error("Error al disparar webhook de creación:", webhookErr);
          });
        }
      }
    } catch (err) {
      console.error(err);
      toast.success("✅ Cita agendada localmente");
    } finally {
      setIsSubmitting(false);
      setIsAddModalOpen(false);
      setFormData(initialFormState);
    }
  };

  // Actualizar Cita
  const handleUpdateAppointment = async (e) => {
    e.preventDefault();
    if (!editingApp) return;

    setIsSubmitting(true);
    const updatedList = appointments.map(app => 
      app.id === editingApp.id ? { ...editingApp } : app
    );
    setAppointments(updatedList);
    saveToLocal(updatedList);

    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          client: editingApp.client,
          phone: editingApp.phone,
          service: editingApp.service,
          staffassigned: editingApp.staffassigned,
          date: editingApp.date,
          time: editingApp.time,
          status: editingApp.status
        })
        .eq('id', editingApp.id);

      if (error) console.warn("[Supabase App Update Warning]:", error.message);
      toast.success("Cita actualizada exitosamente");
    } catch (err) {
      console.error(err);
      toast.success("Cita actualizada localmente");
    } finally {
      setIsSubmitting(false);
      setEditingApp(null);
    }
  };

  // Cambiar Estado Rápido
  const handleQuickStatusChange = async (appId, newStatus) => {
    const updatedList = appointments.map(app =>
      app.id === appId ? { ...app, status: newStatus } : app
    );
    setAppointments(updatedList);
    saveToLocal(updatedList);

    try {
      await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appId);
      toast.success(`Cita marcada como: ${newStatus}`);
    } catch (err) {
      console.warn("Status update fallback:", err);
      toast.success(`Cita marcada como: ${newStatus}`);
    }
  };

  // Confirmar Anticipo / Depósito
  const handleConfirmDeposit = async (appointmentId, depositAmount) => {
    const montoTexto = Number(depositAmount || 0).toLocaleString('es-CO');
    if (!window.confirm(`¿Confirmar que recibiste $${montoTexto} de anticipo?`)) return;

    // Guardar el estado previo para poder revertir si falla la escritura
    const previousList = appointments;
    const updatedList = appointments.map(a =>
      a.id === appointmentId ? { ...a, deposit_status: 'confirmed' } : a
    );
    setAppointments(updatedList);
    saveToLocal(updatedList);

    const revert = () => {
      setAppointments(previousList);
      saveToLocal(previousList);
    };

    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({ deposit_status: 'confirmed' })
        .eq('id', appointmentId)
        .select('id');

      if (error) {
        console.error('[Deposit Confirm Error]', error.message);
        revert();
        toast.error('No se pudo confirmar el anticipo. Intenta de nuevo.');
        return;
      }

      // Sin error pero 0 filas actualizadas (RLS / id inexistente) => fallo silencioso
      if (!data || data.length === 0) {
        console.error('[Deposit Confirm Error] La actualización no afectó ninguna fila', appointmentId);
        revert();
        toast.error('No se pudo confirmar el anticipo. Intenta de nuevo.');
        return;
      }

      toast.success('Anticipo confirmado');
    } catch (err) {
      console.error('[Deposit Confirm Error]', err);
      revert();
      toast.error('No se pudo confirmar el anticipo. Intenta de nuevo.');
    }
  };

  // Eliminar Cita
  const handleDeleteAppointment = async (appId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta cita de la agenda?")) return;

    try {
      // Fetch the absolute latest google_event_id from Supabase before deleting
      const { data: latestData } = await supabase
        .from('appointments')
        .select('google_event_id')
        .eq('id', appId)
        .single();

      const realGoogleEventId = latestData?.google_event_id;

      // Actualizar UI optimistamente
      const updatedList = appointments.filter(app => app.id !== appId);
      setAppointments(updatedList);
      saveToLocal(updatedList);

      // 1. Enviar petición a n8n para borrar el evento en Google Calendar (si existe y tenemos webhook)
      const webhookUrl = import.meta.env.VITE_N8N_DELETE_WEBHOOK_URL;
      
      if (webhookUrl && realGoogleEventId) {
        try {
          await fetch(webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              business_id: activeBusiness.id,
              google_event_id: realGoogleEventId
            })
          });
          console.log("Evento enviado a eliminar en Google Calendar:", realGoogleEventId);
        } catch (webhookErr) {
          console.error("Error al disparar webhook de eliminación:", webhookErr);
        }
      }

      // 2. Eliminar de Supabase localmente
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appId);

      if (error) throw error;
      toast.success("Cita eliminada correctamente.");
    } catch (err) {
      console.error('Error eliminando cita:', err);
      toast.error("Error eliminando cita.");
      fetchAppointments(); // Revertir UI en caso de fallo DB
    }
  };

  // Enviar Recordatorio WhatsApp
  const handleSendWhatsAppReminder = (app) => {
    const phoneClean = (app.phone || '').replace(/\D/g, '');
    if (!phoneClean) {
      toast.error("Esta cita no tiene un número de teléfono válido.");
      return;
    }
    const businessName = activeBusiness?.nombre_negocio || 'Nuestro Negocio';
    const message = `Hola *${app.client}* 👋 Te escribimos de *${businessName}* para recordarte tu cita agendada:\n\n📌 *Servicio:* ${app.service}\n📅 *Fecha:* ${app.date}\n⏰ *Hora:* ${app.time}\n👤 *Especialista:* ${app.staffassigned || 'Equipo'}\n\n¿Nos confirmas tu asistencia respondiendo a este mensaje? ¡Te esperamos! ✨`;
    const url = `https://wa.me/${phoneClean}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Filtrar Citas
  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = tomorrowDate.toISOString().split('T')[0];

  const filteredAppointments = useMemo(() => {
    return appointments.filter(app => {
      // 1. Buscador texto
      const search = searchTerm.toLowerCase();
      const matchSearch = 
        (app.client || '').toLowerCase().includes(search) ||
        (app.phone || '').includes(search) ||
        (app.service || '').toLowerCase().includes(search) ||
        (app.staffassigned || '').toLowerCase().includes(search);

      // 2. Filtro Estado
      const matchStatus = filterStatus === 'all' || app.status === filterStatus;

      // 3. Filtro Especialista
      const matchStaff = filterStaff === 'all' || app.staffassigned === filterStaff;

      // 4. Filtro Rango Temporal
      let matchDate = true;
      if (filterDateRange === 'today') {
        matchDate = app.date === todayStr;
      } else if (filterDateRange === 'tomorrow') {
        matchDate = app.date === tomorrowStr;
      } else if (filterDateRange === 'future') {
        matchDate = app.date >= todayStr;
      } else if (filterDateRange === 'past') {
        matchDate = app.date < todayStr;
      } else if (filterDateRange === 'this_week') {
        const appD = new Date(app.date);
        const curr = new Date();
        const diffDays = Math.ceil((appD - curr) / (1000 * 60 * 60 * 24));
        matchDate = diffDays >= 0 && diffDays <= 7;
      }

      return matchSearch && matchStatus && matchStaff && matchDate;
    });
  }, [appointments, searchTerm, filterStatus, filterStaff, filterDateRange, todayStr, tomorrowStr]);

  // Contadores KPI
  const stats = useMemo(() => {
    const todayCount = appointments.filter(a => a.date === todayStr && a.status !== 'Cancelada').length;
    const confirmedCount = appointments.filter(a => a.status === 'Confirmada').length;
    const pendingCount = appointments.filter(a => a.status === 'Pendiente').length;
    const completedCount = appointments.filter(a => a.status === 'Completada').length;
    return { todayCount, confirmedCount, pendingCount, completedCount, total: appointments.length };
  }, [appointments, todayStr]);

  // Helpers de Formato de Estado
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Confirmada':
      case 'scheduled':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
            <CheckCircle2 size={12} /> Confirmada
          </span>
        );
      case 'Pendiente':
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-950/40 border border-amber-500/30 text-amber-400">
            <AlertCircle size={12} /> Pendiente
          </span>
        );
      case 'Completada':
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-950/40 border border-blue-500/30 text-blue-400">
            <Check size={12} /> Completada
          </span>
        );
      case 'Cancelada':
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-950/40 border border-red-500/30 text-red-400">
            <XCircle size={12} /> Cancelada
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-seryi-bg-light border border-seryi-text/30 text-seryi-text">
            {status}
          </span>
        );
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'whatsapp_ia':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">
            🤖 WhatsApp IA
          </span>
        );
      case 'minisitio_web':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-md border border-cyan-500/20">
            🌐 Web Booking
          </span>
        );
      case 'panel_manual':
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-md border border-purple-500/20">
            🖥️ Panel Manual
          </span>
        );
    }
  };

  if (!activeBusiness) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="flex items-center gap-3 text-seryi-text animate-pulse">
          <RefreshCw className="animate-spin" size={20} />
          <span>Cargando agenda del negocio...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-7xl mx-auto p-4 md:p-6 animate-in fade-in duration-300">
      
      {/* 1. HEADER PRINCIPAL */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-gradient-to-r from-seryi-bg-light/80 via-seryi-bg-light/40 to-seryi-bg-dark p-6 rounded-2xl border border-seryi-green/15 shadow-xl backdrop-blur-md">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-seryi-green/10 border border-seryi-green/30 flex items-center justify-center text-seryi-green shadow-[0_0_15px_rgba(0,227,138,0.15)]">
              <CalendarDays size={26} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-seryi-heading tracking-tight flex items-center gap-2">
                Agenda Inteligente & Citas
                <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-seryi-green/10 text-seryi-green border border-seryi-green/30">
                  {activeBusiness.nombre_negocio}
                </span>
              </h2>
              <p className="text-seryi-text text-sm mt-0.5">
                Control centralizado de reservas de tu Asistente WhatsApp IA, Minisitio y Registro Manual.
              </p>
            </div>
          </div>
        </div>

        {/* BOTONES DE ACCIÓN RÁPIDA */}
        <div className="flex flex-wrap items-center gap-3">
          <button 
            onClick={syncAppointmentsToGoogle}
            disabled={loading}
            title="Sincronizar reservas locales con Google Calendar y la nube"
            className="flex items-center gap-2 bg-seryi-bg-dark border border-seryi-bg-light hover:border-seryi-green/40 text-seryi-text hover:text-seryi-heading text-sm font-semibold px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin text-seryi-green' : ''} />
            <span className="hidden sm:inline">{loading ? 'Sincronizando...' : 'Actualizar'}</span>
          </button>

          <button 
            onClick={async () => {
              try {
                toast.loading("Conectando con Google...", { id: "oauth" });
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: window.location.hostname === 'localhost' ? `${window.location.origin}/dashboard` : 'https://keyla.seryiads.com/dashboard',
                    scopes: 'https://www.googleapis.com/auth/calendar.events',
                    queryParams: {
                      access_type: 'offline',
                      prompt: 'consent'
                    }
                  }
                });
                if (error) throw error;
              } catch (err) {
                toast.error("Error iniciando vinculación: " + err.message, { id: "oauth" });
              }
            }}
            title="Vincular o reconectar Google Calendar"
            className="flex items-center gap-2 bg-seryi-bg-dark border border-seryi-bg-light hover:border-blue-500/40 text-seryi-text hover:text-blue-400 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all"
          >
            <CalendarDays size={16} />
            <span className="hidden sm:inline">Vincular Calendario</span>
          </button>

          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 bg-seryi-green hover:bg-[#00c978] text-[#041424] text-sm font-extrabold px-5 py-2.5 rounded-xl shadow-[0_0_20px_rgba(0,227,138,0.25)] hover:shadow-[0_0_25px_rgba(0,227,138,0.4)] transition-all transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
          >
            <Plus size={18} strokeWidth={3} />
            <span>➕ Agendar Nueva Cita</span>
          </button>
        </div>
      </div>

      {/* 2. TARJETAS DE MÉTRICAS KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-seryi-bg-dark/70 border border-seryi-bg-light/60 p-4 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <span className="text-xs font-bold text-seryi-text uppercase tracking-wider block">Citas de Hoy</span>
            <span className="text-2xl font-black text-seryi-heading mt-1 block">{stats.todayCount}</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-seryi-green/10 border border-seryi-green/20 flex items-center justify-center text-seryi-green">
            <Clock size={20} />
          </div>
        </div>

        <div className="bg-seryi-bg-dark/70 border border-emerald-500/20 p-4 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <span className="text-xs font-bold text-emerald-400/80 uppercase tracking-wider block">Confirmadas</span>
            <span className="text-2xl font-black text-emerald-400 mt-1 block">{stats.confirmedCount}</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <CheckCircle2 size={20} />
          </div>
        </div>

        <div className="bg-seryi-bg-dark/70 border border-amber-500/20 p-4 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <span className="text-xs font-bold text-amber-400/80 uppercase tracking-wider block">Por Confirmar</span>
            <span className="text-2xl font-black text-amber-400 mt-1 block">{stats.pendingCount}</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
            <AlertCircle size={20} />
          </div>
        </div>

        <div className="bg-seryi-bg-dark/70 border border-seryi-bg-light/60 p-4 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <span className="text-xs font-bold text-seryi-text uppercase tracking-wider block">Total en Agenda</span>
            <span className="text-2xl font-black text-seryi-heading mt-1 block">{stats.total}</span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
            <Calendar size={20} />
          </div>
        </div>
      </div>

      {/* 3. BARRA DE HERRAMIENTAS Y FILTROS */}
      <div className="bg-seryi-bg-dark/80 border border-seryi-bg-light p-4 rounded-2xl flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between shadow-md">
        
        {/* Buscador */}
        <div className="relative flex-1 min-w-[220px]">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-seryi-text">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Buscar por cliente, teléfono, servicio o staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading placeholder:text-seryi-text/50 text-sm rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:border-seryi-green/50 focus:ring-1 focus:ring-seryi-green/30 transition-all"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-seryi-text hover:text-seryi-heading"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filtros Dropdown */}
        <div className="flex flex-wrap items-center gap-2.5">
          
          {/* Filtro Rango Temporal */}
          <div className="relative">
            <select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              className="bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-xs font-semibold rounded-xl pl-3 pr-8 py-2.5 appearance-none focus:outline-none focus:border-seryi-green/50 cursor-pointer"
            >
              <option value="all">📅 Todos los días</option>
              <option value="today">⭐ Hoy ({stats.todayCount})</option>
              <option value="tomorrow">☀️ Mañana</option>
              <option value="this_week">🗓️ Próximos 7 días</option>
              <option value="future">🔮 Próximas</option>
              <option value="past">⏳ Historial Pasadas</option>
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-seryi-text pointer-events-none" />
          </div>

          {/* Filtro Estado */}
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-xs font-semibold rounded-xl pl-3 pr-8 py-2.5 appearance-none focus:outline-none focus:border-seryi-green/50 cursor-pointer"
            >
              <option value="all">⚡ Todos los estados</option>
              <option value="Confirmada">🟢 Confirmadas</option>
              <option value="Pendiente">🟡 Pendientes</option>
              <option value="Completada">🔵 Completadas</option>
              <option value="Cancelada">🔴 Canceladas</option>
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-seryi-text pointer-events-none" />
          </div>

          {/* Filtro Especialista */}
          {activeBusiness?.staff && activeBusiness.staff.length > 0 && (
            <div className="relative">
              <select
                value={filterStaff}
                onChange={(e) => setFilterStaff(e.target.value)}
                className="bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-xs font-semibold rounded-xl pl-3 pr-8 py-2.5 appearance-none focus:outline-none focus:border-seryi-green/50 cursor-pointer max-w-[150px] truncate"
              >
                <option value="all">👤 Todo el Staff</option>
                {activeBusiness.staff.map(s => (
                  <option key={s.id || s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-seryi-text pointer-events-none" />
            </div>
          )}

          {/* Toggle Vista */}
          <div className="flex items-center bg-seryi-bg p-0.5 rounded-xl border border-seryi-bg-light">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg text-xs font-bold transition-all ${viewMode === 'grid' ? 'bg-seryi-bg-light text-seryi-green shadow-sm' : 'text-seryi-text hover:text-seryi-heading'}`}
              title="Vista Cuadrícula"
            >
              <Layers size={15} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg text-xs font-bold transition-all ${viewMode === 'table' ? 'bg-seryi-bg-light text-seryi-green shadow-sm' : 'text-seryi-text hover:text-seryi-heading'}`}
              title="Vista Lista Compacta"
            >
              <ListFilter size={15} />
            </button>
          </div>

        </div>
      </div>

      {/* 4. LISTADO O TABLA DE CITAS */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-seryi-bg-dark/40 rounded-2xl border border-seryi-bg-light border-dashed">
          <RefreshCw size={32} className="text-seryi-green animate-spin mb-4" />
          <p className="text-seryi-text font-medium animate-pulse">Sincronizando agenda...</p>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-seryi-bg-dark/50 rounded-2xl border border-seryi-bg-light border-dashed text-center">
          <div className="w-16 h-16 rounded-full bg-seryi-bg-light flex items-center justify-center text-seryi-text/40 mb-4">
            <Calendar size={32} />
          </div>
          <h3 className="text-seryi-heading text-lg font-bold mb-1">No hay citas en este filtro</h3>
          <p className="text-seryi-text text-sm max-w-md mx-auto mb-5">
            {searchTerm || filterStatus !== 'all' || filterDateRange !== 'all'
              ? 'No se encontraron reservas que coincidan con los criterios seleccionados.'
              : 'Aún no tienes citas registradas. Puedes agendar una manualmente desde el botón superior o esperar reservas de tu bot de WhatsApp.'}
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
              setFilterDateRange('all');
              setFilterStaff('all');
              setIsAddModalOpen(true);
            }}
            className="flex items-center gap-2 bg-seryi-green text-[#041424] text-xs font-extrabold px-4 py-2 rounded-xl"
          >
            <Plus size={16} strokeWidth={3} />
            <span>Agendar Primera Cita</span>
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        /* --- VISTA CUADRÍCULA (CARDS) --- */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAppointments.map(app => {
            const isToday = app.date === todayStr;
            const isPast = app.date < todayStr;

            return (
              <div 
                key={app.id} 
                className={`group relative bg-seryi-bg-dark/90 border rounded-2xl p-5 transition-all duration-200 flex flex-col justify-between overflow-hidden shadow-sm hover:shadow-md ${
                  isToday 
                    ? 'border-seryi-green/40 hover:border-seryi-green bg-gradient-to-b from-seryi-bg-dark to-seryi-bg-light/30 shadow-[0_0_15px_rgba(0,227,138,0.05)]' 
                    : isPast
                    ? 'border-seryi-bg-light/50 opacity-80'
                    : 'border-seryi-bg-light hover:border-seryi-green/30'
                }`}
              >
                {/* Indicador superior de Hoy */}
                {isToday && (
                  <div className="absolute top-0 inset-x-0 h-1 bg-seryi-green shadow-[0_0_10px_#00e38a]"></div>
                )}

                <div>
                  {/* Encabezado de la Tarjeta */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        {getSourceBadge(app.source)}
                        {isToday && (
                          <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-seryi-green text-[#041424]">
                            HOY
                          </span>
                        )}
                      </div>
                      <h4 className="text-seryi-heading font-black text-base truncate flex items-center gap-1.5">
                        <User size={16} className="text-seryi-green shrink-0" />
                        {app.client || 'Cliente Sin Nombre'}
                      </h4>
                      {app.phone && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-seryi-text font-mono">+{app.phone}</span>
                          <button
                            onClick={() => handleSendWhatsAppReminder(app)}
                            title="Enviar recordatorio o mensaje por WhatsApp"
                            className="text-emerald-400 hover:text-emerald-300 p-1 hover:bg-emerald-500/10 rounded transition-colors"
                          >
                            <MessageSquare size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      {getStatusBadge(app.status)}
                    </div>
                  </div>

                  {/* Detalles del Servicio y Horario */}
                  <div className="space-y-2.5 my-3 py-3 border-y border-seryi-bg-light/60 text-xs">
                    
                    {/* Servicio */}
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-seryi-bg-light flex items-center justify-center text-seryi-text shrink-0">
                        <Sparkles size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] uppercase font-bold text-seryi-text/70 block">Servicio</span>
                        <span className="text-seryi-heading font-semibold truncate block">{app.service}</span>
                      </div>
                    </div>

                    {/* Fecha y Hora */}
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-seryi-green/10 flex items-center justify-center text-seryi-green shrink-0">
                        <Clock size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] uppercase font-bold text-seryi-text/70 block">Fecha & Horario</span>
                        <span className="text-seryi-heading font-semibold block">
                          {app.date} • <span className="text-seryi-green font-bold">{app.time}</span>
                        </span>
                      </div>
                    </div>

                    {/* Especialista / Staff */}
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400 shrink-0">
                        <UserCircle2 size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] uppercase font-bold text-seryi-text/70 block">Atendido por</span>
                        <span className="text-seryi-heading font-medium truncate block">{app.staffassigned || 'Cualquiera / Por asignar'}</span>
                      </div>
                    </div>

                  </div>

                  {/* Anticipo / Saldo Pendiente */}
                  {app.deposit_amount > 0 && (
                    <div className="flex items-center justify-between text-[11px] mt-1">
                      <span className="text-seryi-text">
                        Anticipo: <span className="font-bold text-seryi-heading">${Number(app.deposit_amount).toLocaleString('es-CO')}</span>
                        {app.deposit_status === 'declared' && (
                          <span className="ml-1.5 text-amber-400">⚠️ Declarado</span>
                        )}
                        {app.deposit_status === 'confirmed' && (
                          <span className="ml-1.5 text-emerald-400">✅ Confirmado</span>
                        )}
                      </span>
                      {app.deposit_status === 'declared' && (
                        <button
                          onClick={() => handleConfirmDeposit(app.id, app.deposit_amount)}
                          className="text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold px-2 py-0.5 rounded-lg"
                        >
                          Confirmar
                        </button>
                      )}
                    </div>
                  )}
                  {app.deposit_amount > 0 && (() => {
                    // El precio del servicio no se guarda en la cita: se resuelve contra el catálogo del negocio
                    const resolvedPrice = app.precio_cobrado ??
                      activeBusiness?.services?.find(s => s.nombre === app.service)?.precio;

                    if (resolvedPrice === null || resolvedPrice === undefined || resolvedPrice === '') {
                      return (
                        <div className="text-[11px] text-amber-400 mt-0.5">
                          Configura el precio de este servicio para calcular el saldo
                        </div>
                      );
                    }

                    return (
                      <div className="text-[11px] text-seryi-text mt-0.5">
                        Saldo pendiente: <span className="font-bold text-seryi-heading">
                          ${calculateSaldoPendiente(resolvedPrice, app.deposit_amount, app.deposit_status).toLocaleString('es-CO')}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Acciones de la Tarjeta */}
                <div className="pt-2 flex items-center justify-between gap-2">
                  
                  {/* Selector Rápido de Estado */}
                  <div className="relative">
                    <select
                      value={app.status}
                      onChange={(e) => handleQuickStatusChange(app.id, e.target.value)}
                      className="bg-seryi-bg border border-seryi-bg-light text-[11px] font-bold text-seryi-text hover:text-seryi-heading rounded-lg pl-2 pr-6 py-1 appearance-none focus:outline-none focus:border-seryi-green/40 cursor-pointer"
                    >
                      <option value="Confirmada">🟢 Confirmada</option>
                      <option value="Pendiente">🟡 Pendiente</option>
                      <option value="Completada">🔵 Completada</option>
                      <option value="Cancelada">🔴 Cancelada</option>
                    </select>
                    <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-seryi-text pointer-events-none" />
                  </div>

                  <div className="flex items-center gap-1">
                    {/* Botón WhatsApp */}
                    <button
                      onClick={() => handleSendWhatsAppReminder(app)}
                      title="Enviar recordatorio por WhatsApp"
                      className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 transition-all cursor-pointer"
                    >
                      <Send size={13} />
                    </button>

                    {/* Botón Editar */}
                    <button
                      onClick={() => setEditingApp(app)}
                      title="Editar Cita"
                      className="p-1.5 rounded-lg bg-seryi-bg hover:bg-seryi-bg-light text-seryi-text hover:text-seryi-heading border border-seryi-bg-light transition-all cursor-pointer"
                    >
                      <Edit3 size={13} />
                    </button>

                    {/* Botón Eliminar */}
                    <button
                      onClick={() => handleDeleteAppointment(app.id)}
                      title="Eliminar Cita"
                      className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all cursor-pointer"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                </div>

              </div>
            );
          })}
        </div>
      ) : (
        /* --- VISTA TABLA / LISTA COMPACTA --- */
        <div className="bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl overflow-x-auto shadow-md">
          <table className="w-full text-left text-sm">
            <thead className="bg-seryi-bg-light/60 text-xs uppercase font-bold text-seryi-text border-b border-seryi-bg-light">
              <tr>
                <th className="py-3 px-4">Cliente & Contacto</th>
                <th className="py-3 px-4">Servicio</th>
                <th className="py-3 px-4">Fecha & Hora</th>
                <th className="py-3 px-4">Especialista</th>
                <th className="py-3 px-4">Origen</th>
                <th className="py-3 px-4">Estado</th>
                <th className="py-3 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-seryi-bg-light/40">
              {filteredAppointments.map(app => {
                const isToday = app.date === todayStr;
                return (
                  <tr key={app.id} className={`hover:bg-seryi-bg-light/30 transition-colors ${isToday ? 'bg-seryi-green/5' : ''}`}>
                    
                    {/* Cliente */}
                    <td className="py-3 px-4">
                      <div className="font-bold text-seryi-heading flex items-center gap-1.5">
                        {app.client}
                        {isToday && (
                          <span className="text-[9px] font-black uppercase px-1 py-0.2 rounded bg-seryi-green text-[#041424]">HOY</span>
                        )}
                      </div>
                      <div className="text-xs text-seryi-text font-mono mt-0.5">+{app.phone}</div>
                    </td>

                    {/* Servicio */}
                    <td className="py-3 px-4">
                      <span className="font-medium text-seryi-heading">{app.service}</span>
                    </td>

                    {/* Fecha y Hora */}
                    <td className="py-3 px-4">
                      <div className="font-semibold text-seryi-heading">{app.date}</div>
                      <div className="text-xs text-seryi-green font-bold">{app.time}</div>
                    </td>

                    {/* Especialista */}
                    <td className="py-3 px-4 text-seryi-text font-medium">
                      {app.staffassigned || 'Cualquiera'}
                    </td>

                    {/* Origen */}
                    <td className="py-3 px-4">
                      {getSourceBadge(app.source)}
                    </td>

                    {/* Estado */}
                    <td className="py-3 px-4">
                      <select
                        value={app.status}
                        onChange={(e) => handleQuickStatusChange(app.id, e.target.value)}
                        className="bg-seryi-bg border border-seryi-bg-light text-xs font-bold text-seryi-heading rounded-lg px-2 py-1 focus:outline-none focus:border-seryi-green/50 cursor-pointer"
                      >
                        <option value="Confirmada">🟢 Confirmada</option>
                        <option value="Pendiente">🟡 Pendiente</option>
                        <option value="Completada">🔵 Completada</option>
                        <option value="Cancelada">🔴 Cancelada</option>
                      </select>
                    </td>

                    {/* Acciones */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleSendWhatsAppReminder(app)}
                          title="WhatsApp Reminder"
                          className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                        >
                          <Send size={13} />
                        </button>
                        <button
                          onClick={() => setEditingApp(app)}
                          title="Editar"
                          className="p-1.5 rounded-lg bg-seryi-bg hover:bg-seryi-bg-light text-seryi-text hover:text-seryi-heading border border-seryi-bg-light"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => handleDeleteAppointment(app.id)}
                          title="Eliminar"
                          className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. MODAL AGENDAR NUEVA CITA MANUAL                                        */}
      {/* ========================================================================= */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-seryi-bg-dark border border-seryi-green/30 w-full max-w-lg rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Header Modal */}
            <div className="flex items-center justify-between p-5 bg-seryi-bg-light/60 border-b border-seryi-bg-light">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-seryi-green/10 text-seryi-green flex items-center justify-center">
                  <Plus size={18} strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-seryi-heading">Agendar Nueva Cita</h3>
                  <p className="text-xs text-seryi-text">Ingreso manual directo al panel de control</p>
                </div>
              </div>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="p-1.5 rounded-lg text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Formulario */}
            <form onSubmit={handleCreateAppointment} className="p-5 space-y-4 overflow-y-auto">
              
              {/* Nombre del Cliente */}
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                  Nombre del Cliente <span className="text-seryi-green">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-seryi-text">
                    <User size={15} />
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Juan Sebastián Pérez"
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:border-seryi-green/50 focus:ring-1 focus:ring-seryi-green/30"
                  />
                </div>
              </div>

              {/* Teléfono / WhatsApp */}
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                  WhatsApp / Celular <span className="text-seryi-green">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-seryi-text">
                    <Phone size={15} />
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="Ej: 573001234567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:border-seryi-green/50 focus:ring-1 focus:ring-seryi-green/30"
                  />
                </div>
                <span className="text-[10px] text-seryi-text/70 mt-1 block">Incluye el código de país (ej: 57 para Colombia).</span>
              </div>

              {/* Servicio */}
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                  Servicio a Realizar <span className="text-seryi-green">*</span>
                </label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50 cursor-pointer"
                >
                  <option value="">-- Seleccionar del Catálogo --</option>
                  {(activeBusiness?.services || []).map(svc => (
                    <option key={svc.id || svc.nombre} value={svc.nombre}>
                      {svc.nombre} {svc.precio ? `($${Number(svc.precio).toLocaleString('es-CO')})` : '(A cotizar)'}
                    </option>
                  ))}
                  <option value="OTRO">✍️ Otro servicio personalizado...</option>
                </select>

                {formData.service === 'OTRO' && (
                  <input
                    type="text"
                    required
                    placeholder="Escribe el nombre del servicio..."
                    value={formData.customService}
                    onChange={(e) => setFormData({ ...formData, customService: e.target.value })}
                    className="mt-2 w-full bg-seryi-bg border border-seryi-green/40 text-seryi-heading text-sm rounded-xl px-3 py-2 focus:outline-none"
                  />
                )}
              </div>

              {/* Especialista / Staff */}
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                  Especialista Asignado
                </label>
                <select
                  value={formData.staffassigned}
                  onChange={(e) => setFormData({ ...formData, staffassigned: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50 cursor-pointer"
                >
                  <option value="">Cualquiera / Sin Asignar</option>
                  {(activeBusiness?.staff || []).map(st => (
                    <option key={st.id || st.name} value={st.name}>
                      👤 {st.name} {st.specialty ? `(${st.specialty})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha y Hora en 2 Columnas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                    Fecha <span className="text-seryi-green">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                    Hora <span className="text-seryi-green">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: 10:30 AM"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                  />
                </div>
              </div>

              {/* Chips de Horas Rápidas */}
              <div>
                <span className="text-[10px] font-bold uppercase text-seryi-text block mb-1">Horarios frecuentes:</span>
                <div className="flex flex-wrap gap-1.5">
                  {quickTimeSlots.map(slot => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setFormData({ ...formData, time: slot })}
                      className={`text-[11px] font-semibold px-2 py-1 rounded-lg border transition-all cursor-pointer ${
                        formData.time === slot
                          ? 'bg-seryi-green text-[#041424] border-seryi-green'
                          : 'bg-seryi-bg-light/40 border-seryi-bg-light text-seryi-text hover:text-seryi-heading hover:border-seryi-green/30'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estado Inicial */}
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1.5">
                  Estado Inicial
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'Confirmada' })}
                    className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                      formData.status === 'Confirmada'
                        ? 'bg-emerald-950/60 border-emerald-500 text-emerald-400'
                        : 'bg-seryi-bg border-seryi-bg-light text-seryi-text'
                    }`}
                  >
                    <CheckCircle2 size={14} /> Confirmada
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'Pendiente' })}
                    className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                      formData.status === 'Pendiente'
                        ? 'bg-amber-950/60 border-amber-500 text-amber-400'
                        : 'bg-seryi-bg border-seryi-bg-light text-seryi-text'
                    }`}
                  >
                    <AlertCircle size={14} /> Pendiente
                  </button>
                </div>
              </div>

              {/* Botón Submit */}
              <div className="pt-3 border-t border-seryi-bg-light flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-seryi-bg-light text-seryi-text hover:text-seryi-heading text-xs font-bold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl bg-seryi-green hover:bg-[#00c978] text-[#041424] text-xs font-extrabold shadow-lg shadow-seryi-green/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                  <span>{isSubmitting ? 'Guardando...' : 'Guardar y Agendar Cita'}</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 6. MODAL EDITAR CITA                                                      */}
      {/* ========================================================================= */}
      {editingApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-seryi-bg-dark border border-seryi-bg-light w-full max-w-lg rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Header Modal */}
            <div className="flex items-center justify-between p-5 bg-seryi-bg-light/60 border-b border-seryi-bg-light">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
                  <Edit3 size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-seryi-heading">Modificar Cita</h3>
                  <p className="text-xs text-seryi-text">Editar detalles de la reserva seleccionada</p>
                </div>
              </div>
              <button 
                onClick={() => setEditingApp(null)}
                className="p-1.5 rounded-lg text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Formulario Edición */}
            <form onSubmit={handleUpdateAppointment} className="p-5 space-y-4 overflow-y-auto">
              
              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Cliente</label>
                <input
                  type="text"
                  required
                  value={editingApp.client || ''}
                  onChange={(e) => setEditingApp({ ...editingApp, client: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1">WhatsApp / Teléfono</label>
                <input
                  type="text"
                  required
                  value={editingApp.phone || ''}
                  onChange={(e) => setEditingApp({ ...editingApp, phone: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Servicio</label>
                <input
                  type="text"
                  required
                  value={editingApp.service || ''}
                  onChange={(e) => setEditingApp({ ...editingApp, service: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Especialista / Staff</label>
                <input
                  type="text"
                  value={editingApp.staffassigned || ''}
                  onChange={(e) => setEditingApp({ ...editingApp, staffassigned: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Fecha</label>
                  <input
                    type="date"
                    required
                    value={editingApp.date || ''}
                    onChange={(e) => setEditingApp({ ...editingApp, date: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Hora</label>
                  <input
                    type="text"
                    required
                    value={editingApp.time || ''}
                    onChange={(e) => setEditingApp({ ...editingApp, time: e.target.value })}
                    className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-seryi-text mb-1">Estado</label>
                <select
                  value={editingApp.status || 'Confirmada'}
                  onChange={(e) => setEditingApp({ ...editingApp, status: e.target.value })}
                  className="w-full bg-seryi-bg border border-seryi-bg-light text-seryi-heading text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-seryi-green/50 cursor-pointer"
                >
                  <option value="Confirmada">🟢 Confirmada</option>
                  <option value="Pendiente">🟡 Pendiente</option>
                  <option value="Completada">🔵 Completada</option>
                  <option value="Cancelada">🔴 Cancelada</option>
                </select>
              </div>

              {/* Botones */}
              <div className="pt-3 border-t border-seryi-bg-light flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setEditingApp(null)}
                  className="px-4 py-2 rounded-xl border border-seryi-bg-light text-seryi-text hover:text-seryi-heading text-xs font-bold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-xl bg-seryi-green text-[#041424] text-xs font-extrabold shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
                  <span>Guardar Cambios</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

    </div>
  );
}
